import { Controller } from "@hotwired/stimulus"
import { CrepeBuilder } from "@milkdown/crepe/builder"
import { toolbar } from "@milkdown/crepe/feature/toolbar"
import { listItem } from "@milkdown/crepe/feature/list-item"
import { linkTooltip } from "@milkdown/crepe/feature/link-tooltip"
import { cursor } from "@milkdown/crepe/feature/cursor"
import { inlineMarkdownPlugin } from "../plugins/inline-markdown-plugin"
import { exitMarkPlugin } from "../plugins/exit-mark-plugin"
import { placeholder } from "@milkdown/crepe/feature/placeholder"
import { docChangePlugin } from "../plugins/doc-change-plugin"
import { gfmWithoutFootnotesPlugin } from "../plugins/gfm-without-footnotes"
import { trailingConfig } from "@milkdown/kit/plugin/trailing"
import { EditorMount } from "../lib/editor_mount"
// Crepe theme CSS is imported in main.css

export default class extends Controller {
	static targets = ["editor"]
	static values = { content: String, placeholder: String }

	#mount = null

	async connect() {
		this.#mount = new EditorMount(() => this.#buildEditor())
		const builder = await this.#mount.mount()
		// A disconnect or a newer mount landed while this one was still being
		// created, and EditorMount has already destroyed it.
		if (!builder) return

		// Listen for focus/blur on the ProseMirror contenteditable
		const pm = this.editorTarget.querySelector(".ProseMirror")
		if (pm) {
			pm.addEventListener("focusin", () => {
				this.element.dispatchEvent(
					new CustomEvent("crepe:focus", { bubbles: true })
				)
			})
			pm.addEventListener("focusout", () => {
				this.element.dispatchEvent(
					new CustomEvent("crepe:blur", { bubbles: true })
				)
			})
		}
	}

	// Turbo caches the DOM rather than ProseMirror's state, so leave the
	// markdown in the content value: the next mount reads it back.
	disconnect() {
		const editor = this.#mount?.active
		if (editor) this.contentValue = editor.getMarkdown()
		this.#mount?.unmount()
	}

	#buildEditor() {
		const builder = new CrepeBuilder({
			root: this.editorTarget,
			defaultValue: this.contentValue || "",
		})

		builder
			.addFeature(toolbar)
			.addFeature(listItem)
			.addFeature(linkTooltip)
			.addFeature(cursor)
			.addFeature(placeholder, { text: this.placeholderValue, mode: "doc" })

		builder.editor.config((ctx) => {
			// Crepe otherwise keeps an empty paragraph under any note that ends
			// in a list, table or code block, and puts it straight back when it
			// is deleted.
			ctx.update(trailingConfig.key, (config) => ({
				...config,
				shouldAppend: () => false,
			}))
		})

		const emitChange = () =>
			this.element.dispatchEvent(new CustomEvent("crepe:change", { bubbles: true }))

		builder.editor
			.use(gfmWithoutFootnotesPlugin)
			.use(docChangePlugin(emitChange))
			.use(inlineMarkdownPlugin)
			.use(exitMarkPlugin)

		return builder
	}

	// Answers from the content value once the editor is gone, which is when
	// note-card asks while flushing a save on teardown.
	getMarkdown() {
		return this.#mount?.active?.getMarkdown() || this.contentValue || ""
	}
}
